/**
 * PricingSection component - Concise pricing section for landing page
 * Shows 3 main plans with link to full pricing page
 */

import {Link} from 'react-router-dom';
import {Container} from '../ui/Container';
import {Section} from '../ui/Section';
import {Card} from '../ui/Card';
import {Button} from '../ui/Button';
import {ROUTES} from '../../config/constants';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

const plans = [
  {
    name: 'Free',
    price: '$0',
    description: 'Perfect for trying out VoxaPilot',
    features: ['10 hours/month', 'Basic AI summaries', 'Up to 3 team members'],
    cta: {label: 'Start Free', href: ROUTES.SIGN_IN},
  },
  {
    name: 'Pro',
    price: '$29',
    description: 'For individuals and small teams',
    features: ['100 hours/month', 'Advanced AI summaries', 'Unlimited team members'],
    cta: {label: 'Get Started', href: `${ROUTES.SIGN_IN}?plan=pro`},
    popular: true,
  },
  {
    name: 'Pro+',
    price: '$59',
    description: 'For power users',
    features: ['300 hours/month', 'Premium AI features', 'Advanced analytics'],
    cta: {label: 'Get Pro+', href: `${ROUTES.SIGN_IN}?plan=pro-plus`},
  },
];

export function PricingSection() {
  const {ref, isVisible} = useScrollAnimation();

  return (
    <Section id="pricing" padding="lg" background="white" ref={ref} className="bg-background dark:bg-[#1E1E1E]">
      <Container>
        <div className={`transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {/* Header */}
          <div className="text-center mb-16">
            <p className="text-sm font-medium text-orange-600 dark:text-orange-500 mb-3 uppercase tracking-wide">
              Pricing
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-textSecondary dark:text-[#858585] max-w-2xl mx-auto">
              Choose the plan that works for your team. All plans include a free trial.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
            {plans.map((plan, index) => (
              <div
                key={plan.name}
                className={`transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{transitionDelay: `${index * 0.1}s`}}
              >
                <Card
                  hover={plan.popular}
                  className={`p-8 h-full flex flex-col relative transition-all duration-300 ${
                    plan.popular 
                      ? 'border-2 border-gray-900 dark:border-gray-700 shadow-xl dark:shadow-gray-900/50 scale-105' 
                      : 'border border-gray-200 dark:border-gray-700 hover:shadow-lg dark:hover:shadow-gray-900/30'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-gray-900 dark:bg-gray-700 text-white px-4 py-1.5 rounded-full text-xs font-semibold shadow-md">
                        Popular
                      </span>
                    </div>
                  )}

                  <div className="flex flex-col h-full">
                    <div className="mb-8">
                      <h3 className="text-2xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-3">
                        {plan.name}
                      </h3>
                      <p className="text-sm text-textSecondary dark:text-[#858585] mb-6">
                        {plan.description}
                      </p>
                      <div className="flex items-baseline mb-2">
                        <span className="text-4xl font-bold text-textPrimary dark:text-[#CCCCCC]">
                          {plan.price}
                        </span>
                        <span className="text-base text-textSecondary dark:text-[#858585] ml-2">
                          /mo
                        </span>
                      </div>
                      {plan.price !== '$0' && (
                        <p className="text-xs text-textTertiary dark:text-[#6A6A6A]">
                          billed monthly
                        </p>
                      )}
                    </div>

                    <ul className="space-y-3 flex-1 mb-8">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-start text-sm text-textSecondary dark:text-[#858585]">
                          <svg 
                            className="w-5 h-5 text-gray-900 dark:text-gray-400 mr-3 mt-0.5 flex-shrink-0" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Link to={plan.cta.href} className="block mt-auto">
                      <Button
                        variant={plan.popular ? 'primary' : 'outline'}
                        size="lg"
                        className={`w-full ${
                          plan.popular
                            ? 'bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 text-white shadow-md hover:shadow-lg transition-all'
                            : 'hover:border-gray-900 dark:hover:border-gray-600'
                        }`}
                      >
                        {plan.cta.label}
                      </Button>
                    </Link>
                  </div>
                </Card>
              </div>
            ))}
          </div>

          {/* Link to full pricing */}
          <div className="text-center pt-4">
            <Link
              to={ROUTES.PRICING}
              className="text-textPrimary dark:text-[#CCCCCC] font-medium hover:text-orange-600 dark:hover:text-orange-500 transition-colors inline-flex items-center gap-2 group"
            >
              View all plans and features
              <svg 
                className="w-4 h-4 transition-transform group-hover:translate-x-1" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </Container>
    </Section>
  );
}

